import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'

import { DATE_FIELDS, MODULE_FIELD_TYPES } from '../../constants'
import { useRequest } from '../../hooks/UseRequest'
import { handleErrorResponse, generateErrorBody } from '../../helpers/errorHandling'
import { validateSpecialForm, formDataSanitizer, getConditionalWatches, getFieldRenderRules } from '../../helpers/formValidation'
import { setNotification } from '../../redux/notificationSlice'
import SubheaderDivider from '../../components/elements/SubheaderDivider'
import SpecialInstruction from '../../components/elements/SpecialInstruction'
import DisplayField from '../../components/elements/DisplayField'
import FormFieldBuilder from '../../components/FormFieldBuilder'
import Loading from '../../components/Loading'

/**
 * The edit form for a single module record. Split out of ModuleDetail so that
 * its `react-hook-form` instance is scoped to one record. ModuleDetail mounts
 * this with a per-record `key`, so each record (and each edit session) gets a
 * brand-new form — RHF never carries a previous record's field values into the
 * next record's save. See ModuleDetail.test.jsx for the regression this guards.
 *
 * Lock handling, dashlets, subpanels and the read-only view stay in ModuleDetail;
 * this component owns only the form state and the save/cancel handlers.
 *
 * @param {object} props
 * @param {object} props.module        the module metadata
 * @param {Array}  props.moduleFields  sorted module fields
 * @param {object} props.row           the record being edited
 * @param {object} props.dropDowns     dropdown slice (for system dropdown options)
 * @param {Array}  props.dropdownOptions  dropdown options indexed by id
 * @param {object} props.active        current user slice
 * @param {Function} props.onSaved     called with the updated record on success
 * @param {Function} props.onCancel    called when the user cancels the edit
 * @returns {JSX.Element}
 */
export default function ModuleEditForm({
  module, moduleFields, row, dropDowns, dropdownOptions, active, onSaved, onCancel,
}) {
  let countDivider = 0
  const {
    register, handleSubmit, control, setError, formState: { errors }, watch,
  } = useForm()
  const [saving, setSaving] = useState(false)
  const params = useParams()
  const dispatch = useDispatch()
  const requestsForm = useRequest()

  // Seed the dependent-field state from the record on mount. Fresh per record
  // because this component remounts per record (keyed in ModuleDetail).
  const [stateFields, setStateFields] = useState(() => {
    const init = []
    moduleFields?.forEach((m) => {
      if (row[m.column_name]) {
        init.push({ name: `${module.code_key}_${m.column_name}`, value: row[m.column_name] })
      }
    })
    return init
  })

  const handleStateFields = (name, value) => {
    const stateFieldName = `${module.code_key}_${name}`
    if (stateFields.some((item) => item.name === stateFieldName)) {
      setStateFields(stateFields.map((item) => (
        stateFieldName === item?.name ? { ...item, value } : item
      )))
    } else {
      setStateFields([...stateFields, { name: stateFieldName, value }])
    }
  }

  const dispatchErorr = () => {
    if (Object.keys(errors).length > 0) {
      const body = generateErrorBody(errors, moduleFields)
      dispatch(setNotification({ type: 'error', body }))
    }
  }

  const beforeFormSubmit = (event) => {
    event.preventDefault()
    dispatchErorr()
    handleSubmit(onSubmit)()
  }

  useEffect(() => {
    dispatchErorr()
  }, [errors, saving])

  const onSubmit = async (formData) => {
    const formError = validateSpecialForm(module.code_key, formData, dropdownOptions, setError)
    if (formError) return

    setSaving(true)
    const sanitizedData = formDataSanitizer(formData, module, dropdownOptions)
    requestsForm.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/${module.code_key}/${params.id}`,
    )

    try {
      const response = await requestsForm.edit(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: 'Successfully saved your changes' }))
        setSaving(false)
        onSaved(response.data.data)
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      if (error?.response?.status == 403 || error?.response?.status == 412) {
        // this is a custom error message
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      } else {
        handleErrorResponse(error.response?.data?.errors, setError)
      }
    }
  }

  // Special-field reveal rules. Edit defaults to the record's values (row[column_name]) — load-bearing
  // so e.g. email_from is visible on load for an existing email template. The tag divergence is
  // preserved: should_create_tag_in_rc is hard-hidden below and rc_tag_id has no rule here (createOnly),
  // so it is always shown — unlike the create form.
  const watchedValues = getConditionalWatches(module.code_key, false, watch, (name) => row[name])

  return (
    <form onSubmit={beforeFormSubmit} className="lox-form px-1 py-1">
      <div className="columns is-multiline pt-3">
        {moduleFields.map((m) => {
          // Hide the should_create_tag_in_rc field
          if (m.module_code_key === 'tag' && m.column_name === 'should_create_tag_in_rc') {
            return ''
          }

          const fieldType = dropdownOptions[m.module_field_type_dropdown_option_id]?.code_key
          // Filter ID, Date and Show More columns
          if (DATE_FIELDS.includes(m.column_name) || m.column_name === 'id') return ''
          if (fieldType === MODULE_FIELD_TYPES.SHOWMORE.code_key) return ''

          if (fieldType === MODULE_FIELD_TYPES.SUBHEADER.code_key) {
            countDivider += 1
            return (
              <SubheaderDivider
                order={countDivider}
                name={m.label_name}
                key={m.id}
              />
            )
          }

          if (fieldType === MODULE_FIELD_TYPES.SPECIAL_INSTRUCTIONS.code_key) {
            return <SpecialInstruction name={m.label_name} key={m.id} content={m.helper_text} />
          }

          const isActiveColumn = m.column_name.replace('_id', '_is_active')
          const selectOptions = {
            dropdownOptions: dropDowns?.data.find(
              (e) => m.dropdown_key === e.code_key,
            )?.dropdown_options,
            defaultValue: {
              name: row[m.column_name.replace('_id', '_label')],
              id: row[m.column_name],
              ...(isActiveColumn in row && { is_active: row[isActiveColumn] }),
            },
            is_editable: (m.is_editable && !m.is_dev_only) || (m.is_editable && m.is_dev_only && active?.data?.user_role_label == 'Developers') || !params.id,
            fieldType,
          }

          const fr = active.data.user_role_field_restrictions

          if ((m.is_editable && m.is_dev_only && active?.data?.user_role_label != 'Developers')
            || !m.is_editable
            || (fr && fr[module.code_key] && fr[module.code_key].find((e) => e === m.column_name))) {
            return (
              <>
                <input
                  name={m.column_name}
                  type="hidden"
                  defaultValue={row[m.column_name]}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...((m.is_editable == 0 || (m.is_required == 1 && ((m.is_editable && !m.is_dev_only) || (m.is_editable && m.is_dev_only && active?.data?.user_role_label == 'Developers')))) && register(m.column_name, { required: m.is_required }))}
                />
                <DisplayField
                  data={m}
                  row={row}
                  key={m.id}
                  dropdowns={dropDowns}
                  dropdownOptions={dropdownOptions}
                />
              </>
            )
          }

          // Conditional show/hide is centralized in SPECIAL_FIELD_RULES.
          let fieldNotice = null
          const { hidden } = getFieldRenderRules(m, watchedValues, dropdownOptions, false)
          const showFormFieldBuilder = !hidden

          if (m.module_code_key === 'exclusion_list' && m.column_name === 'bank_routing_number_id') {
            fieldNotice = (
              <p>
                <Link target="_blank" to="/bank_routing_number/form">
                  Create New Bank Routing Number
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="is-size-12 ml-1" />
                </Link>
              </p>
            )
          }
          return (showFormFieldBuilder && (
            <FormFieldBuilder
              field={m}
              data={row[m.column_name]}
              selectOptions={selectOptions}
              register={register}
              errors={errors}
              control={control}
              key={m.id}
              row={row}
              fieldNotice={fieldNotice}
              handleStateFields={handleStateFields}
              stateFields={stateFields}
            />
          ))
        })}
      </div>
      <div className="lox-form__buttons-wrapper has-text-right">
        <span
          className="has-text-weight-medium has-text-link"
          aria-hidden="true"
          onClick={onCancel}
        >
          Cancel
        </span>
        <button
          type="submit"
          className="button is-link is-small ml-5"
          data-testid="module-form-submit"
          disabled={saving && 'disabled'}
        >
          {saving ? (
            <>
              Saving
              <Loading size="is-size-6" />
            </>
          ) : 'Save'}
        </button>
      </div>
    </form>
  )
}
